import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import { searchCapsules, searchUsers, signOut } from '../lib/supabase'
import CapsuleCard from '../components/CapsuleCard'
import './Feed.css'
import './Search.css'

const TABS = [
  { key: 'depth_desc', label: 'Most Depth' },
  { key: 'depth_asc',  label: 'Least Depth' },
  { key: 'latest',     label: 'Latest' },
  { key: 'users',      label: 'Users' },
]

export default function Search() {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const initialQuery = searchParams.get('q') || ''
  const [inputValue, setInputValue] = useState(initialQuery)
  const [activeTab, setActiveTab] = useState('depth_desc')

  const [capsuleResults, setCapsuleResults] = useState({
    depth_desc: [], depth_asc: [], latest: []
  })
  const [userResults, setUserResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [currentQuery, setCurrentQuery] = useState(initialQuery)

  const inputRef = useRef(null)

  // Run search on mount if q is in the URL
  useEffect(() => {
    if (initialQuery) runSearch(initialQuery)
    inputRef.current?.focus()
  }, [])

  const runSearch = async (q) => {
    if (!q.trim()) return
    setLoading(true)
    setSearched(true)
    setCurrentQuery(q.trim())
    try {
      const [depthDesc, depthAsc, latest, users] = await Promise.all([
        searchCapsules(q, 'depth_desc'),
        searchCapsules(q, 'depth_asc'),
        searchCapsules(q, 'latest'),
        searchUsers(q),
      ])
      setCapsuleResults({ depth_desc: depthDesc, depth_asc: depthAsc, latest })
      setUserResults(users)
    } catch (e) {
      console.error('Search failed:', e.message)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!inputValue.trim()) return
    setSearchParams({ q: inputValue.trim() })
    runSearch(inputValue.trim())
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }

  const getInitials = (name) =>
    name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?'

  const currentCapsules = capsuleResults[activeTab] || []

  return (
    <div className="search-page">
      {/* Header */}
      <header className="feed-header">
        <div className="feed-logo" style={{ cursor: 'pointer' }} onClick={() => navigate('/feed')}>
          Agora
          <span className="feed-logo-beta">Beta</span>
        </div>

        {/* Search bar in header */}
        <form className="feed-search-form" onSubmit={handleSubmit}>
          <span className="feed-search-icon">⌕</span>
          <input
            ref={inputRef}
            className="feed-search-input"
            placeholder="Search topics, ideas, people…"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
          />
          {inputValue && (
            <button
              type="button"
              className="feed-search-clear"
              onClick={() => { setInputValue(''); inputRef.current?.focus() }}
            >×</button>
          )}
        </form>

        <div className="feed-header-right">
          <button className="feed-profile-btn" onClick={() => navigate('/profile')}>
            <div
              className="feed-avatar"
              style={{ background: profile?.avatar_color || '#152550' }}
            >
              {getInitials(profile?.display_name)}
            </div>
            <span>{profile?.display_name || user?.email}</span>
          </button>
          <button className="feed-signout" onClick={handleSignOut}>Sign out</button>
        </div>
      </header>

      <div className="search-body">
        {/* No search yet */}
        {!searched && (
          <div className="search-empty-state">
            <div className="search-empty-glyph">⌕</div>
            <div className="search-empty-title">Search Agora</div>
            <div className="search-empty-body">
              Find capsules by topic, keyword, or idea. Discover users by name or handle.
            </div>
          </div>
        )}

        {/* Results */}
        {searched && (
          <>
            <div className="search-results-header">
              <span className="search-query-label">
                Results for <strong>"{currentQuery}"</strong>
              </span>
            </div>

            {/* Tabs */}
            <div className="search-tabs">
              {TABS.map(tab => (
                <button
                  key={tab.key}
                  className={`search-tab ${activeTab === tab.key ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                  {tab.key !== 'users' && !loading && (
                    <span className="search-tab-count">
                      {capsuleResults[tab.key]?.length || 0}
                    </span>
                  )}
                  {tab.key === 'users' && !loading && (
                    <span className="search-tab-count">{userResults.length}</span>
                  )}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="search-loading"><span className="spinner" /></div>
            ) : (
              <>
                {/* Capsule tabs */}
                {activeTab !== 'users' && (
                  <>
                    {currentCapsules.length === 0 ? (
                      <div className="search-no-results">
                        <div className="search-no-results-glyph">◇</div>
                        <div>No capsules found for <strong>"{currentQuery}"</strong></div>
                      </div>
                    ) : (
                      <div className="search-capsules">
                        {currentCapsules.map(c => (
                          <CapsuleCard key={c.id} capsule={c} />
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Users tab */}
                {activeTab === 'users' && (
                  <>
                    {userResults.length === 0 ? (
                      <div className="search-no-results">
                        <div className="search-no-results-glyph">◇</div>
                        <div>No users found matching <strong>"{currentQuery}"</strong></div>
                      </div>
                    ) : (
                      <div className="search-users-list">
                        {userResults.map(u => (
                          <UserRow
                            key={u.id}
                            user={u}
                            onClick={() => navigate(`/user/${u.username}`)}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function UserRow({ user, onClick }) {
  const getInitials = (name) =>
    name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?'

  return (
    <div className="search-user-row" onClick={onClick}>
      <div
        className="search-user-avatar"
        style={{ background: user.avatar_color || '#152550' }}
      >
        {getInitials(user.display_name)}
      </div>
      <div className="search-user-info">
        <div className="search-user-name">{user.display_name || user.username}</div>
        <div className="search-user-handle">@{user.username}</div>
        {user.bio && <div className="search-user-bio">{user.bio}</div>}
      </div>
      <div className="search-user-meta">
        <div className="search-user-depth">{user.depth_score || 0}</div>
        <div className="search-user-depth-label">depth</div>
        {user.capsule_count > 0 && (
          <div className="search-user-caps">{user.capsule_count} capsules</div>
        )}
      </div>
      <div className="search-user-arrow">→</div>
    </div>
  )
}
